import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { Spin, Button, Table } from "antd";
import "../../../shared/styles/InfoOrder.css";
import { useTranslation } from "react-i18next";

import velanLogo from "../../../shared/assets/velan-plastic-logo.jpg";
import barcodeImg from "../../../shared/assets/velan-barcode.jpg";

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
const InfoOrder = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [systemSettings, setSystemSettings] = useState(() => readCachedSystemConfig());

  useEffect(() => {
    let cancelled = false;
    fetchSystemConfig()
      .then((config) => {
        if (!cancelled) setSystemSettings(config);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const infoRowStyle = {
    margin: 0,
    fontSize: 16,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "6px",
    direction: "rtl",
    width: "100%",
  };
  const hasSubUnits = useMemo(() => {
    return orderDetails?.order_products?.some(
      (item) => item.sub_qty != null && item.sub_qty !== 0
    );
  }, [orderDetails?.order_products]);
  useEffect(() => {
    apiClient
      .get(`/orders/${id}`)
      .then((response) => {
        setOrderDetails(response.data);
      })
      .catch((error) => {
        console.error("Error fetching order details:", error);
      });
  }, [id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const baseColumns = [
    {
      title: <b>#</b>,
      key: "rowNum",
      width: 50,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: <b>{t("تفاصيل")}</b>,
      dataIndex: "description",
      key: "description",
      align: "center",
      render: (text) => <span>{text || <b>{t("noDescription")}</b>}</span>,
    },
    {
      title: <b>{t("عدد گونیة")}</b>,
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
    },
    {
      title: <b>{t("سعر گونية")}</b>,
      dataIndex: "singlePrice",
      key: "singlePrice",
      align: "center",
      render: (text) => <span>{text}</span>,
    },
    {
      title: <b>{t("مجموع النوعية")}</b>,
      dataIndex: "subTotal",
      key: "subTotal",
      align: "center",
      render: (text) => <span>{text}</span>,
    },
  ];
  const subUnitColumns = useMemo(() => ([
    {
      title: <b>{t(" نوعية")}</b>,
      dataIndex: "unitSub",
      key: "unitSub",
      align: "center",
      render: (text) => (
        <span>{text === "-" ? "-" : text || <b>{t("N/A")}</b>}</span>
      ),
    },
    {
      title: <b>{t(" عدد ١ گونیةکە")}</b>,
      dataIndex: "ratio",
      key: "ratio",
      align: "center",
      render: (text) => (
        <span>{text === "-" ? "-" : text || <b>{t("N/A")}</b>}</span>
      ),
    },
    {
      title: <b>{t("مجموع الربطة")}</b>,
      dataIndex: "totalSubQty",
      key: "totalSubQty",
      align: "center",
      render: (text) => (
        <span>
          {text === "-" ? "-" : text != null ? text : <b>{t("N/A")}</b>}
        </span>
      ),
    },
    {
      title: <b>{t("سعر الربطة")}</b>,
      dataIndex: "priceSubUnit",
      key: "priceSubUnit",
      align: "center",
      render: (text) => (
        <span>{text === "-" ? "-" : text || <b>{t("N/A")}</b>}</span>
      ),
    },
  ]), [t]);

  const columns = useMemo(() => {
    if (hasSubUnits) {
      const cols = [...baseColumns];
      cols.splice(2, 0, ...subUnitColumns);
      return cols;
    }
    return baseColumns;
  }, [hasSubUnits, baseColumns, subUnitColumns]);
  const data = useMemo(() => {
    const mainCode = (systemSettings || readCachedSystemConfig())?.currency_code || "IQD";
    return (
      orderDetails?.order_products?.map((item, index) => {
        const useSub =
          item.sub_qty && item.sub_qty > 0 && item.sub_price && item.sub_price > 0;

        const formatNumber = (number) => {
          // Round to 2 decimal places and remove trailing zeros
          const rounded = Math.round(number * 100) / 100;
          return Number(rounded).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          });
        };

        const quantity = item.quantity;
        // Always use single_price from the order item
        const singlePrice = `${mainCode} ${formatNumber(item.single_price)} `;

        const unitSubName =
          !item.sub_qty || item.sub_qty === 0
            ? t("N/A")
            : item.product?.sub_unit?.name
              ? (() => {
                try {
                  const subUnitObj = JSON.parse(item.product.sub_unit.name);
                  return (
                    subUnitObj[i18n.language] ||
                    subUnitObj.en ||
                    Object.values(subUnitObj).find(Boolean) ||
                    t("N/A")
                  );
                } catch (error) {
                  console.error("Error parsing sub-unit name:", error);
                  return t("N/A");
                }
              })()
              : t("N/A");
        const ratio =
          !item.sub_qty || item.sub_qty === 0
            ? "-"
            : item.product?.sub_unit_ratio;
        const totalSubQty =
          !item.sub_qty || item.sub_qty === 0 ? "-" : item.sub_qty;
        const priceSubUnit =
          !item.sub_qty || item.sub_qty === 0
            ? "-"
            : item.sub_price != null
              ? `${mainCode} ${formatNumber(item.sub_price)}`
              : t("N/A");

        // Use the total_price directly from the order item (saved in database)
        // instead of recalculating to avoid floating-point precision errors
        const subTotalValue = item.total_price || item.subtotal ||
          (useSub && item.sub_qty != null && item.sub_price != null
            ? item.sub_price * item.sub_qty
            : item.single_price * item.quantity);

        const subTotal = `${mainCode} ${formatNumber(subTotalValue)}`;
        return {
          key: index,
          description: (() => {
            try {
              if (item.product?.name) {
                const nameObj = JSON.parse(item.product.name);
                // Try current language, fallback to any available, fallback to en, fallback to value itself
                return nameObj[i18n.language] || nameObj.en || Object.values(nameObj).find(Boolean) || t("No product available");
              }
            } catch (e) {
              // If parsing fails, fallback to raw value
              return item.product?.name || t("No product available");
            }
            return t("No product available");
          })(),
          quantity: `${quantity}`,
          unitSub: unitSubName,
          ratio: ratio,
          totalSubQty: totalSubQty,
          priceSubUnit: priceSubUnit,
          singlePrice: singlePrice,
          subTotal: subTotal,
        };
      }) || []
    );
  }, [orderDetails, i18n.language, t, systemSettings]);

  if (!orderDetails) {
    return (
      <div>
        <Spin tip={t("Loading...")}>
          <div style={{ width: "100%", height: "100vh" }} />
        </Spin>
      </div>
    );
  }

  const cfg = systemSettings || readCachedSystemConfig() || {};
  const mainCurrencyCode = cfg.currency_code || "IQD";
  const secondaryCurrencyCode = cfg.secondary_currency_code || "";

  const companyTitle =
    (cfg.invoice_company_name || "").trim() ||
    (cfg.site_name || "").trim() ||
    t(" معمل ڤیلان");
  const tagline = (cfg.invoice_tagline || "").trim();
  const headerLogoSrc = cfg.invoice_logo_url || cfg.sidebar_logo_url || velanLogo;
  const watermarkEnabled = cfg.invoice_watermark_enabled !== false;
  const watermarkOpacity =
    typeof cfg.invoice_watermark_opacity === "number" ? cfg.invoice_watermark_opacity : 0.07;
  const stampSrc = cfg.invoice_stamp_image_url || barcodeImg;

  const displayEmail = (cfg.invoice_email || "").trim() || "info@velan-plastic.com";
  const displayWeb = (cfg.invoice_website || "").trim() || "http://velan-plastic.com";
  const phoneLines = (cfg.invoice_phones || "").trim()
    ? cfg.invoice_phones.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : ["0750 456 7566", "0770 234 1022"];
  const addressText = (cfg.invoice_address || "").trim() || t("factoryAddress");
  const footerNote = (cfg.invoice_footer_note || "").trim();

  return (
    <>
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "0px" }}
        className="no-print"
      >
        <Button
          onClick={() => window.print()}
          style={{
            padding: "12px 20px",
            fontSize: "18px",
            fontWeight: "bold",
            backgroundColor: "#1890ff",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "background 0.3s, transform 0.2s",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "300px",
            textAlign: "center",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#40a9ff")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#1890ff")
          }
          onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.95)")}
          onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          <span role="img" aria-label="print">
            🖨️
          </span>{" "}
          <b>{t("Print")}</b>
        </Button>
      </div>

      <div style={{ textAlign: "center", marginTop: "0" }}>
        <div
          id="delivery-note"
          className="delivery-note-screen"
          style={{
            width: "210mm",
            margin: "auto",
            padding: "5px",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          {watermarkEnabled && (
            <img
              src={headerLogoSrc}
              alt={t("Company Logo")}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(20deg)",
                opacity: watermarkOpacity,
                width: "70%",
                zIndex: 0,
                pointerEvents: "none",
                userSelect: "none",
              }}
              className="print-watermark"
            />
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <div style={{ textAlign: "right" }}>
              <h3 style={{ margin: 0, marginRight: 50, fontSize: 35 }}>
                <b>{companyTitle}</b>
              </h3>
              {tagline ? (
                <p style={{ margin: "4px 50px 0 0", fontSize: 14, fontWeight: 600 }}>
                  {tagline}
                </p>
              ) : null}
            </div>

            <div style={{ textAlign: "left" }}>
              <img
                src={headerLogoSrc}
                alt={t("Company Logo")}
                style={{ height: 80, width: 250, marginLeft: 80, objectFit: "contain" }}
              />
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px 24px",
              marginBottom: "20px",
              textAlign: "right",
              alignItems: "start",
            }}
          >
            <p style={infoRowStyle} dir="rtl">
              <b>{t("تاريخ الطلب")}</b>
              <b>:</b>
              <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                {dayjs(orderDetails.created_at).format("DD/MM/YYYY")} at {dayjs(orderDetails.created_at).format("hh:mm A")}
              </b>
            </p>
            <p style={infoRowStyle} dir="rtl">
              <b>{t("الزبون")}</b>
              <b>:</b>
              <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                {orderDetails.customer?.name || ""}
              </b>
            </p>
            <p style={infoRowStyle} dir="rtl">
              <b>{t("رقم الهاتف ")}</b>
              <b>:</b>
              <b dir="ltr" style={{ unicodeBidi: "isolate" }}>
                {orderDetails.customer?.phone_number || ""}
              </b>
            </p>
            {orderDetails.accountant &&
              typeof orderDetails.accountant === "string" &&
              orderDetails.accountant !== "" ? (
              <p style={infoRowStyle} dir="rtl">
                <b>{t("المحاسب")}</b>
                <b>:</b>
                <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                  {orderDetails.accountant}
                </b>
              </p>
            ) : orderDetails.accountant && orderDetails.accountant.name ? (
              <p style={infoRowStyle} dir="rtl">
                <b>{t("المحاسب")}</b>
                <b>:</b>
                <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                  {orderDetails.accountant.name}
                </b>
              </p>
            ) : orderDetails.accountant_id &&
              orderDetails.accountant_id !== "" ? (
              <p style={infoRowStyle} dir="rtl">
                <b>{t("المحاسب")}</b>
                <b>:</b>
                <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                  {orderDetails.accountant_id}
                </b>
              </p>
            ) : (
              <p style={infoRowStyle} />
            )}
          </div>
          <Table
            columns={columns.map((col) => ({
              ...col,
              onCell: (record, rowIndex) => ({
                style: {
                  padding: "0px 8px",
                  fontSize: "12px",
                },
              }),
            }))}
            dataSource={data}
            pagination={false}
            className="custom-table"
            style={{
              marginBottom: 0,
              padding: 0,
              textAlign: "center",
            }}
            bordered
          />
          <div
            id="order-summary"
            className="order-summary-block"
            style={{
              marginLeft: "10px",
              marginRight: "10px",
              paddingBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <img
              src={stampSrc}
              alt={t("Barcode")}
              style={{ height: 70, width: 70, marginLeft: 50, objectFit: "contain" }}
            />

            <div style={{ textAlign: "right" }}>
              <p style={{ margin: 0, fontSize: 20 }}>
                <b> {mainCurrencyCode} {orderDetails.total_price} : {t("totalLabel")} </b>
              </p>
              <p style={{ margin: 0, fontSize: 20 }}>
                <b> {mainCurrencyCode} {orderDetails.amount_paid} : {t("totalPaidLabel")}</b>
              </p>
              {(orderDetails.amount_paid_main != null || orderDetails.amount_paid_secondary != null) && (
                <p style={{ margin: 0, fontSize: 16 }}>
                  <b>
                    {t("paymentDetailsLabel")}:
                    {" "}
                    {orderDetails.amount_paid_main != null
                      ? `${Number(orderDetails.amount_paid_main).toLocaleString()} ${mainCurrencyCode}`
                      : `0 ${mainCurrencyCode}`}
                    {orderDetails.amount_paid_secondary
                      ? ` + ${Number(orderDetails.amount_paid_secondary).toLocaleString()} ${secondaryCurrencyCode || "secondary"}`
                      : ""}
                    {orderDetails?.exchange_rate?.main_per_secondary
                      ? ` (1 ${mainCurrencyCode} = ${Number(orderDetails.exchange_rate.main_per_secondary).toLocaleString()} ${secondaryCurrencyCode || ""})`
                      : ""}
                  </b>
                </p>
              )}
              <p style={{ margin: 0, fontSize: 20 }}>
                <b>
                  {" "}
                  {mainCurrencyCode} {orderDetails.outstanding_balance} : {t("unpaidAmountLabel")}
                </b>
              </p>
              <p style={{ margin: 0, fontSize: 20 }}>
                <b>
                  {mainCurrencyCode} {orderDetails.total_owed_without_current_order} : {t("previousAccountLabel")}
                </b>
              </p>
              <p style={{ margin: 0, fontSize: 20 }}>
                <b>
                  {" "}
                  {mainCurrencyCode} {orderDetails.total_owed_up_to_this_order} : {t("grandTotalLabel")}
                </b>
              </p>
            </div>
          </div>
          {footerNote ? (
            <div
              style={{
                textAlign: "center",
                padding: "8px 16px",
                fontSize: 13,
                whiteSpace: "pre-wrap",
              }}
            >
              {footerNote}
            </div>
          ) : null}
          <div
            id="print-footer"
            className="print-footer-block"
            style={{
              display: "flex",
              justifyContent: "space-between",
              left: "10px",
              right: "10px",
              padding: "0 10px",
            }}
          >
            <div>
              <p style={{ margin: 0, marginLeft: 0, fontSize: 14 }}>
                <b>
                  {" "}
                  {t("Email")}:{displayEmail}
                </b>{" "}
                <br />
                <b>
                  {" "}
                  {t("Web")}:{displayWeb}
                </b>
              </p>
            </div>
            <div>
              {phoneLines.map((line, idx) => (
                <p key={idx} style={{ margin: 0, fontSize: 14 }}>
                  <b>{line}</b>
                </p>
              ))}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: "bold", whiteSpace: "pre-wrap" }}>
                <b>{addressText}</b>
              </p>
            </div>
          </div>
        </div>

        <style>
          {`
        @media print {
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
          }

          @page {
            size: A4;
            margin: 6mm;
          }

          body * {
            visibility: hidden;
          }

          #delivery-note, #delivery-note * {
            visibility: visible;
          }

          .no-print {
            display: none !important;
          }

          /* Hide sidebar and header when printing - layout is full width */
          .app-sidebar,
          .ant-layout-sider,
          .app-header {
            display: none !important;
            visibility: hidden !important;
          }

          .main-layout {
            margin-left: 0 !important;
            width: 100% !important;
          }

          .app-content {
            width: 100% !important;
            padding: 0 !important;
          }

          /* Delivery note: flex column + min page height so totals sit at bottom when content is short */
          #delivery-note {
            position: relative !important;
            top: auto !important;
            left: auto !important;
            width: 100% !important;
            max-width: 198mm !important;
            display: flex !important;
            flex-direction: column !important;
            min-height: calc(297mm - 12mm) !important;
            margin: 0 auto !important;
            padding: 4mm 5mm 22mm 5mm !important;
            box-sizing: border-box;
            font-size: 11pt !important;
            color: black;
          }

          #delivery-note h3 {
            font-size: 15pt !important;
            margin: 0 0 1.5mm 0 !important;
          }

          /* Logos bigger and more visible */
          #delivery-note .header-logo img,
          #delivery-note img[alt]:not(.print-watermark) {
            height: 58px !important;
            width: auto !important;
          }

          #delivery-note p {
            font-size: 10pt !important;
            margin: 0.8mm 0 !important;
          }

          /* Table: 5% smaller; remove fake row / extra border below table */
          #delivery-note .custom-table,
          #delivery-note .ant-table {
            width: 100% !important;
            margin-bottom: 3mm !important;
            font-size: 9pt !important;
            page-break-inside: auto !important;
          }
          #delivery-note .ant-table-placeholder,
          #delivery-note .ant-table-tbody > tr.ant-table-placeholder {
            display: none !important;
          }
          #delivery-note .ant-table-wrapper,
          #delivery-note .ant-table-content {
            border-bottom: none !important;
          }

          #delivery-note .ant-table-thead {
            display: table-header-group !important;
          }

          #delivery-note .ant-table-thead > tr > th {
            padding: 1.4mm 2.4mm !important;
            font-size: 9pt !important;
            font-weight: 700 !important;
            color: #000 !important;
            line-height: 1.2 !important;
            text-align: center !important;
            background: #fff !important;
            border-bottom: 1px solid #000 !important;
          }

          #delivery-note .ant-table-tbody > tr > td,
          #delivery-note .custom-table td,
          #delivery-note .custom-table th {
            padding: 1.4mm 2.4mm !important;
            font-size: 9pt !important;
            line-height: 1.2 !important;
            text-align: center !important;
          }

          #delivery-note .ant-table-tbody > tr {
            page-break-inside: avoid !important;
          }

          /* Totals: stick to bottom of first sheet when table is short; avoid splitting across pages */
          #order-summary {
            position: relative !important;
            bottom: auto !important;
            left: auto !important;
            right: auto !important;
            margin-top: auto !important;
            margin-bottom: 3mm !important;
            padding-bottom: 0 !important;
            background: white !important;
            page-break-inside: avoid !important;
          }

          #order-summary p {
            font-size: 10pt !important;
          }

          #order-summary img {
            height: 52px !important;
            width: 52px !important;
          }

          /* Footer fixed at bottom of every printed page */
          #print-footer {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            margin-top: 0 !important;
            display: flex !important;
            justify-content: space-between !important;
            padding: 2.5mm 6mm !important;
            background: white !important;
            font-size: 9.5pt !important;
            box-shadow: 0 -1px 0 rgba(0,0,0,0.06);
          }

          #print-footer > div > p {
            margin: 0 !important;
            font-size: 9.5pt !important;
          }
        }

        @media screen {
          .delivery-note {
            width: 210mm;
            min-height: 297mm;
            margin: auto;
            padding: 5px;
            box-sizing: border-box;
          }
        }
      `}
        </style>
      </div>
    </>
  );
};
export default InfoOrder;
